import { z } from 'zod';
/**
 * The Integration Cockpit (spec §13.2).
 *
 * Connector TYPES are code: each one is a piece of software QMS360 speaks to.
 * Whether a type is switched on, where it points and in what mode is data, held
 * in `integration_connectors`; its secret is sealed in the credential vault and
 * never returned. One connector per type per organisation.
 */
export const CONNECTOR_TYPES = ['email', 'push', 'sms', 'identity_sync', 'oracle_adw'];
export function isConnectorType(value) {
    return typeof value === 'string' && CONNECTOR_TYPES.includes(value);
}
/**
 * How the connection to the mail server is protected. `none` exists for relays
 * inside a private network; the screen says plainly that it is unencrypted.
 */
export const EMAIL_SECURITY_MODES = ['tls', 'starttls', 'none'];
export const emailConnectorConfigSchema = z.object({
    host: z.string().trim().min(1, 'A mail server is required.').max(253),
    port: z.number().int().min(1).max(65535),
    security: z.enum(EMAIL_SECURITY_MODES),
    /** Empty for a relay that accepts mail without signing in. */
    username: z.string().trim().max(254).nullable().optional(),
    fromAddress: z.string().trim().email('The sender must be an email address.'),
    fromName: z.string().trim().max(120).nullable().optional(),
    /** In sandbox mode every message goes here instead of to its reader. */
    sandboxRecipient: z.string().trim().email().nullable().optional(),
});
export const connectorUpdateSchema = z.object({
    /** Validated against the connector type's own schema by the server. */
    config: z.record(z.string(), z.unknown()).optional(),
    isEnabled: z.boolean().optional(),
    isSandbox: z.boolean().optional(),
});
export const connectorSecretSchema = z.object({
    secret: z.string().min(1, 'Enter the secret.').max(4096),
});
export const connectorTestSchema = z.object({
    /** Also send a test message to the person running the test. */
    sendTestMessage: z.boolean().optional(),
});
export const NOTIFICATION_DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'recorded_not_sent', 'suppressed'];
export const outboxQuerySchema = z.object({
    status: z.enum(NOTIFICATION_DELIVERY_STATUSES).optional(),
    before: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
// ---------------------------------------------------------------------------
// Web Push (spec §13.6)
// ---------------------------------------------------------------------------
/**
 * VAPID identifies this server to the browsers' push services. The subject is
 * a contact they can reach if something goes wrong (a mailto: or https: URL).
 * The public key is generated by the server, never typed; the private key is
 * sealed in the vault.
 */
export const pushConnectorConfigSchema = z.object({
    subject: z
        .string()
        .trim()
        .regex(/^(mailto:[^\s@]+@[^\s@]+|https:\/\/\S+)$/, 'A contact is a mailto: address or an https: link.'),
    publicKey: z.string().regex(/^[A-Za-z0-9_-]{80,100}$/, 'Generate the keys first.').optional(),
});
/** What a browser hands over when it subscribes (PushSubscription.toJSON()). */
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().url().max(2048),
    keys: z.object({
        p256dh: z.string().regex(/^[A-Za-z0-9_-]{80,100}$/),
        auth: z.string().regex(/^[A-Za-z0-9_-]{16,32}$/),
    }),
});
export const pushUnsubscribeSchema = z.object({ endpoint: z.string().url().max(2048) });
//# sourceMappingURL=integrations.js.map